import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Users, Mail, Briefcase, Phone, Pencil, Trash2, Plus } from 'lucide-react';

export function ContactsDialog({
  open, onOpenChange, client, contacts, contactsLoading,
  contactForm, setContactForm, editingContact,
  onSave, onEdit, onDelete, onResetForm,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-[#002FA7]" />
            Contacts — {client?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-5">
          {/* Add/Edit Contact Form */}
          <div className="bg-neutral-50 border border-neutral-200 rounded p-4 space-y-3">
            <p className="text-sm font-medium text-neutral-700">
              {editingContact ? 'Modifier le contact' : 'Ajouter un contact'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="contact-name">Nom *</Label>
                <Input
                  id="contact-name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder="Jean Dupont"
                  className="mt-1"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-email">Email *</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="jean@entreprise.com"
                  className="mt-1"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-function">Fonction</Label>
                <Input
                  id="contact-function"
                  value={contactForm.function}
                  onChange={(e) => setContactForm({ ...contactForm, function: e.target.value })}
                  placeholder="Directeur marketing"
                  className="mt-1"
                  data-testid="contact-function-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-phone">Telephone</Label>
                <Input
                  id="contact-phone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                  className="mt-1"
                  data-testid="contact-phone-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              {editingContact && (
                <Button variant="outline" size="sm" onClick={onResetForm}>
                  Annuler
                </Button>
              )}
              <Button
                size="sm"
                className="bg-[#002FA7] hover:bg-[#002277]"
                onClick={onSave}
                disabled={!contactForm.name || !contactForm.email}
                data-testid="save-contact-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                {editingContact ? 'Mettre a jour' : 'Ajouter'}
              </Button>
            </div>
          </div>

          {/* Contacts List */}
          {contactsLoading ? (
            <div className="p-6 text-center">
              <div className="w-6 h-6 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-neutral-300 rounded">
              <Users className="w-10 h-10 mx-auto mb-2 text-neutral-300" />
              <p className="text-neutral-500 text-sm">Aucun contact pour ce client</p>
              <p className="text-neutral-400 text-xs mt-1">Ajoutez les personnes a qui envoyer les rapports</p>
            </div>
          ) : (
            <div className="border border-neutral-200 rounded divide-y divide-neutral-100" data-testid="contacts-list">
              {contacts.map((contact) => (
                <div key={contact.id} className="p-3 flex items-center justify-between hover:bg-neutral-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#002FA7] text-sm font-bold">
                        {contact.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-neutral-900 text-sm truncate">{contact.name}</p>
                      <div className="flex items-center gap-3 text-xs text-neutral-500">
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="w-3 h-3 flex-shrink-0" />{contact.email}
                        </span>
                        {contact.function && (
                          <span className="flex items-center gap-1 truncate">
                            <Briefcase className="w-3 h-3 flex-shrink-0" />{contact.function}
                          </span>
                        )}
                        {contact.phone && (
                          <span className="flex items-center gap-1 truncate">
                            <Phone className="w-3 h-3 flex-shrink-0" />{contact.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <button
                      onClick={() => onEdit(contact)}
                      className="p-1.5 hover:bg-neutral-200 rounded text-neutral-500"
                      data-testid={`edit-contact-${contact.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(contact.id)}
                      className="p-1.5 hover:bg-red-50 rounded text-neutral-400 hover:text-red-500"
                      data-testid={`delete-contact-${contact.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
